import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContextProvider } from "../components/layout/ThemeProvide.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";
import { setPassword as apiSetPassword } from "../services/authService.js";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { VALIDATION_RULES } from "../utils/validation";

// Validation rules for passwords
const setPasswordValidation = {
  newPassword: {
    required: "Password is required",
    pattern: {
      regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
      message: "Must be 6+ chars, include uppercase, lowercase and number",
    },
  },
  confirmPassword: {
    required: "Please confirm your password",
    matchField: "newPassword",
  },
};

export default function SetPassword() {
  const navigate = useNavigate();
  const { email, otpSent, otpVerified, reset } = useContextProvider();
  const { toast } = useToast();
  const {
    form,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    isValid,
  } = useFormValidation(
    {
      newPassword: "",
      confirmPassword: "",
    },
    setPasswordValidation
  );
  const { newPassword, confirmPassword } = form;
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!email || !otpSent || !otpVerified) {
      navigate("/forgot-password");
    }
  }, [email, otpSent, otpVerified, navigate]);

  const handleSetPassword = async () => {
    if (!validateAll()) {
      toast({
        title: "Validation Error",
        description:
          errors.newPassword ||
          errors.confirmPassword ||
          "Check both password fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsChangingPassword(true);
      const res = await apiSetPassword({ email, newPassword });
      if (!res?.success) {
        toast({
          title: "Failed to set password",
          description: res?.error || "Error occurred.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Password reset successfully!",
        description: "Now login to your account.",
      });
      reset();
      navigate("/");
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Unable to set password",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="flex flex-col justify-between max-h-[100dvh] min-h-[100dvh] p-4 space-y-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-2)}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold">Set Your New Password</h1>
      </div>

      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        <Card className="border-none shadow-sm">
          <CardHeader className="p-0">
            <CardTitle className="text-lg">Password Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* New Password */}
            <div className="relative space-y-2">
              <Label htmlFor="newPassword">Set password</Label>
              <Input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                placeholder="Create password"
                value={newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`pr-10 ${
                  errors.newPassword ? "border-red-500" : ""
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-8 text-muted-foreground"
                onClick={() => setShowNewPassword((p) => !p)}
              >
                {showNewPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
              {touched.newPassword && errors.newPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative mt-2 space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`pr-10 ${
                  errors.confirmPassword ? "border-red-500" : ""
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-8 text-muted-foreground"
                onClick={() => setShowConfirmPassword((p) => !p)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        

         <Card className="border-none shadow-sm">
          <CardContent className="flex justify-center">
            <Button
              type="button"
              className="!w-auto h-8 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
               onClick={handleSetPassword}
              disabled={isChangingPassword}
            >
                {isChangingPassword ? "Changing..." : "Change Password"}
            </Button>
          </CardContent>
        </Card>
      </form>
      <div></div>
    </div>
  );
}
